import { CustomFontSize } from '../components/DialogHeader/DialogHeader';
import { ButtonSkin, Size } from '@wix/design-system';

type Skin = 'light' | 'dark' | 'standard'; // 'light' should be used on dark backgrounds
export type LogoPosition = 'left' | 'center';

type ButtonDesign = {
  skin: ButtonSkin;
  textKey?: string;
  color?: string;
  fullWidth?: boolean;
  height?: string;
  fontSize?: string;
  showArrowOnButton?: boolean;
  hideSuffixIcon?: boolean; // Hide the ChevronRight suffix icon on EmailStep button (default: false, shows icon for horizontal layouts)
};

export type SocialButtonDesign = {
  height?: string;
  borderColor?: string;
  borderWidth?: string;
  borderRadius?: string;
  backgroundColor?: string;
  hoverBackgroundColor?: string;
  hoverBorderColor?: string;
  activeBackgroundColor?: string;
  focusBackgroundColor?: string;
  focusOutline?: string;
  focusOutlineOffset?: string;
  iconTextLayout?: 'centered' | 'space-between';
  fontSize?: string;
};

export type SwitchLinkDesign = {
  color?: string; // Regular link color
  hoverColor?: string; // Hover state color
  focusRingColor?: string; // Focus ring outline color
  underline?: 'none' | 'always' | 'onHover'; // Underline behavior
  fontWeight?: string; // Font weight (e.g., '400', '500', '600')
};

export type DividerDesign = {
  textColor?: string; // Color for "or" text (default: #20303c)
  lineColor?: string; // Color for divider lines (default: #e0e5eb)
};

export type LoaderColors = {
  fill?: string; // Background arc color (--wds-loader-fill)
  fillLoading?: string; // Spinning arc color (--wds-loader-fill-loading)
};

type FormSpacing = {
  inputButtonGap?: string; // Gap between input and button when no forgot email link (CSS value, e.g. '12px', '24px', default '24px')
  inputForgotEmailGap?: string; // Gap between input and forgot email link when present (CSS value, e.g. '8px', '12px', defaults to inputButtonGap)
  forgotEmailButtonGap?: string; // Gap between forgot email link and button (CSS value, e.g. '12px', '24px', default '24px')
  policiesDividerSpacing?: {
    top?: string; // Spacing above horizontal divider before policies (CSS value, e.g. '32px', '44px', default '32px')
    bottom?: string; // Spacing below horizontal divider before policies text (CSS value, e.g. '24px', '44px', default '24px'). Ignored when centerVertically is true.
    centerVertically?: boolean; // When true, vertically centers the policies text between divider and card bottom (default: false)
  };
  titleSectionGap?: string; // Gap between title and subtitle in DialogHeader (CSS value, default: '3px' for vertical, 6px for horizontal)
  forgotEmailAlignment?: 'left' | 'center'; // Alignment of Forgot Email link in EmailStep (default: 'left' for horizontal, 'center' for vertical)
  enterCodeButtonLayout?: 'stacked' | 'horizontal'; // Button layout in EnterCode 2FA dialogs: 'stacked' = vertical full-width buttons (Verify on top, Resend below), 'horizontal' = side-by-side (default)
  // Total visual gap between input and button in secondary dialogs (e.g., SSO enter email)
  // MINIMUM: 15px - values below this are clamped to 15px (due to WDS InputWithLabel internal padding)
  secondaryDialogInputButtonGap?: {
    desktop?: string; // Desktop total gap (CSS value, e.g. '45px', default: '27px', min: '15px')
    mobile?: string; // Mobile total gap (CSS value, e.g. '45px', default: '27px', min: '15px')
  };
};

type Title = {
  key: string;
  font?: string;
  size?: Size;
};

type Container = {
  primary: {
    padding: string;
  };
  secondary: {
    padding: string;
  };
  borderRadius: string;
  boxShadow: string;
  fullScreen?: boolean; // When true, removes card styling and fills screen (for mobile full-screen dialogs)
};

type AuthDialog = {
  title?: Title;
  subtitle?: Title;
  flows?: {
    id: string;
    title: Title;
    subtitle?: Title;
  }[];
  primaryButtonDesign?: ButtonDesign;
  secondaryButtonDesign?: ButtonDesign;
  emailLabelKey?: string;
  dividerTextKey?: string; // Translation key for "or" divider text between social buttons and email input (vertical layout)
};

type SignupAuthDialog = AuthDialog & {
  emailFormButtonTextKey?: string; // Translation key for button in the email form step (with password fields)
};

type LoginAuthDialog = AuthDialog & {
  alreadyHaveAccount?: {
    title?: Title;
    subtitle?: Title;
  };
};

export const SocialProviders = {
  GOOGLE: 'google',
  FACEBOOK: 'facebook',
  APPLE: 'apple',
};

export const PRESETS = {
  DAYFUL: 'dayful',
  EDITORX: 'editorx',
  HOPP: 'hopp',
  EDUCATION_STUDENT: 'EDUCATION_STUDENT',
  EDUCATION_TEACHER: 'EDUCATION_TEACHER',
  CODUX: 'codux', // New branding for WixComponentsStudio (WCS)
  LOGO_MAKER: 'logo_maker',
  BUSINESS_CARD_MAKER: 'bcm',
  WIXEL: 'wixel',
  PICASSO: 'picasso',
  WIX_2025: 'wix-2025',
};

export type ViewPreset = Partial<{
  id: string; // Default - preset key
  nameKey: string; // Default - 'presets.names.wix' = Wix
  skin: Skin; // Default - standard
  shouldDisablePostLoginSuggestions: boolean; // Default - false
  container: {
    layout: 'vertical' | 'horizontal'; // Default - horizontal
    desktop: Container;
    mobile: Container;
  };
  background: string;
  login: LoginAuthDialog;
  signup: SignupAuthDialog;
  landingPage: AuthDialog; // Main landing page (email step)
  logo: Partial<{
    // Default - wix logo
    src: string;
    position: LogoPosition; // Default - 'left'
    height: string; // Default - 24px
    mobile: {
      height: string; // Default - 20px
    };
    alt: string; // Default - 'Wix'
    url: string; // Default - www.wix.com
    dividerColor: string; // Color for vertical divider between main and secondary logo (SSO screens)
  }>;
  policies: {
    policyKey?: string; // Default - 'policies_section.signup_content'
    termsOfUseUrl?: string; // Default - wix terms of use
    privacyPolicyUrl?: string; // Default - wix privacy policy
    linkColor?: string; // Color for policy links (default: inherit)
  };
  buttonDesign?: ButtonDesign;
  socialButtonDesign?: SocialButtonDesign;
  // Values here override socialButtonDesign when new Google SDK FT is enabled
  newGoogleSdkSocialButtonDesign?: SocialButtonDesign;
  // Styling for dialog switch links (Sign Up, Log In, Continue with SSO)
  switchLinkDesign?: SwitchLinkDesign;
  // Styling for "or" divider between social buttons and email input
  dividerDesign?: DividerDesign;
  // Styling for error toast notifications (SectionHelper handles background and text colors)
  errorToastDesign?: {
    iconColor?: string; // Default - '#E62214'
  };
  loaderColors?: {
    fullPage?: LoaderColors;
  };
  formSpacing?: FormSpacing;
  // Social providers separated with comma (for example - 'google,facebook')
  // Default - all ('google,facebook','apple')
  socialProviders: string;
  titleCustomFontSize: {
    authPage: {
      desktop: CustomFontSize;
      mobile: CustomFontSize;
    };
    secondaryFlow: {
      desktop: CustomFontSize;
      mobile: CustomFontSize;
    };
  };
  cssVariables: Record<string, string>;
  useBrandTheme?: boolean; // Default - true. When false, skips WDS Brand theme to allow full CSS variable theming
  secondaryTextColor?: string; // Override WDS Brand theme's hardcoded secondary text color (#595D70 → custom)
  backButtonPosition?: 'header' | 'aboveDialog'; // Default - 'header'. When 'aboveDialog', back button renders above the dialog card instead of in the header
  mobileBackButtonConfig?: {
    position: 'header' | 'hidden'; // Where to show back button on mobile
    marginTop?: string; // Vertical spacing from logo
    marginStart?: string; // Horizontal margin
    iconTextGap?: string; // Gap between arrow icon and text
  };
  dialogVerticalPosition?: 'top' | 'centered'; // Default - 'top'. When 'centered', dialog is vertically centered with minimum top offset
  dialogMinTopOffset?: string; // Minimum distance from top when dialogVerticalPosition is 'centered' (CSS value, e.g. '100px')
}>;

export const skinToColor = {};
export const DEFAULT_LOGO_HEIGHT = '26px';
const COMMON_NEW_GOOGLE_SDK_SOCIAL_BUTTON_DESIGN: SocialButtonDesign = {
  borderColor: '#DADCE0',
  hoverBackgroundColor: '#4285f414',
  hoverBorderColor: '#D2E3FC',
  focusBackgroundColor: '#4285f414',
  focusOutline: '2px solid #00639B',
  focusOutlineOffset: '1px',
};

export const ViewPresets: {
  [key: string]: ViewPreset;
} = {
  [PRESETS.DAYFUL]: {
    nameKey: 'presets.names.dayful',
    landingPage: {
      title: {
        key: 'emailStep.dayful.title',
      },
    },
    signup: {
      title: {
        key: 'signup.title',
      },
      subtitle: {
        key: 'dayful_subtitle',
      },
    },
    logo: {
      src: 'https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/logos/dayful-logo.png',
      height: '34px',
      url: 'https://www.dayful.com/',
    },
  },
  [PRESETS.HOPP]: {
    nameKey: 'presets.names.hopp',
    landingPage: {
      title: {
        key: 'emailStep.hopp.title',
      },
    },
    signup: {
      title: {
        key: 'signup.title',
      },
      subtitle: {
        key: 'hopp_subtitle',
      },
    },
    logo: {
      src: 'https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/Hopp%20by%20wix%20logo%20(1).svg',
      height: '22px',
      url: 'https://www.hopp.co/',
    },
  },
  [PRESETS.EDUCATION_STUDENT]: {
    id: PRESETS.EDUCATION_STUDENT,
    nameKey: 'presets.names.education',
    landingPage: {
      title: {
        key: 'emailStep.education.student.title',
      },
    },
    login: {
      subtitle: {
        key: 'login.education.subtitle',
      },
    },
    signup: {
      title: {
        key: 'signup.education.student.title',
      },
    },
    logo: {
      src: 'https://images-wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/642436ff-70f5-4ebc-8fea-47fab46f7f73/1693247680916',
      url: 'https://www.wix.com/education',
    },
    policies: {
      termsOfUseUrl:
        'https://www.wix.com/tomorrow/classroom/platform/terms-of-use',
      privacyPolicyUrl:
        'https://www.wix.com/tomorrow/classroom/platform/privacy-policy',
    },
    socialProviders: SocialProviders.GOOGLE,
  },
  [PRESETS.EDUCATION_TEACHER]: {
    id: PRESETS.EDUCATION_TEACHER,
    nameKey: 'presets.names.education',
    landingPage: {
      title: {
        key: 'emailStep.education.teacher.title',
      },
    },
    login: {
      subtitle: {
        key: 'login.education.subtitle',
      },
    },
    signup: {
      title: {
        key: 'signup.education.teacher.title',
      },
    },
    logo: {
      src: 'https://images-wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/642436ff-70f5-4ebc-8fea-47fab46f7f73/1693247680916',
      url: 'https://www.wix.com/education',
    },
    policies: {
      termsOfUseUrl:
        'https://www.wix.com/tomorrow/classroom/platform/terms-of-use',
      privacyPolicyUrl:
        'https://www.wix.com/tomorrow/classroom/platform/privacy-policy',
    },
    socialProviders: SocialProviders.GOOGLE,
  },
  [PRESETS.CODUX]: {
    logo: {
      src: 'https://wixplosives.github.io/codux-assets-storage/login/codux-logo.svg',
      position: 'center',
      height: '100px',
      mobile: {
        height: '80px',
      },
      url: 'https://www.codux.com/',
    },
    signup: {
      title: {
        key: 'signup.title',
      },
    },
    policies: {
      termsOfUseUrl: 'https://www.wix.com/about/terms-of-use',
      privacyPolicyUrl: 'https://www.wix.com/about/privacy',
    },
  },
  [PRESETS.EDITORX]: {
    nameKey: 'presets.names.editorx',
    landingPage: {
      title: {
        key: 'emailStep.editorx.title',
      },
    },
    signup: {
      title: {
        key: 'signup.title',
      },
    },
    logo: {
      src: 'https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/logos/EditorX_logo_36x168_black.svg',
      url: 'https://www.editorx.com/',
    },
  },
  [PRESETS.LOGO_MAKER]: {
    nameKey: 'presets.names.logo_maker',
    logo: {
      url: 'https://www.wix.com/logo/maker/',
    },
    landingPage: {
      title: {
        key: 'emailStep.logo_maker.title',
      },
    },
    login: {
      title: {
        key: 'login.logo_maker.title',
      },
    },
    signup: {
      title: {
        key: 'signup.logo_maker.title',
      },
    },
  },
  [PRESETS.BUSINESS_CARD_MAKER]: {
    nameKey: 'presets.names.business_card_maker',
    logo: {
      url: 'https://www.wix.com/tools/business-card-maker/',
    },
    login: {
      title: {
        key: 'login.business_card_maker.title',
      },
    },
    signup: {
      title: {
        key: 'signup.business_card_maker.title',
      },
    },
  },

  [PRESETS.WIXEL]: {
    id: PRESETS.WIXEL,
    shouldDisablePostLoginSuggestions: true,
    nameKey: 'presets.names.wixel',
    container: {
      layout: 'vertical',
      desktop: {
        primary: {
          padding: '48px 0 24px 0',
        },
        secondary: {
          padding: '48px 0 52px 0',
        },
        borderRadius: '24px',
        boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.15)',
      },
      mobile: {
        primary: {
          padding: '36px 0 54px 0',
        },
        secondary: {
          padding: '36px 0 54px 0',
        },
        borderRadius: '24px',
        boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.15)',
      },
    },
    logo: {
      src: 'https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/wixel-logo.svg',
      url: 'https://www.wix.com/wixel',
      height: '28px',
      alt: 'Wixel',
      mobile: {
        height: '24px',
      },
    },
    landingPage: {
      title: {
        key: 'emailStep.wixel.title',
      },
      dividerTextKey: 'emailStep.wixel.or.login.with', // "or" divider between social and email
    },
    login: {
      title: {
        key: 'login.wixel.title',
      },
      subtitle: {
        key: 'login.wixel.subtitle',
      },
      primaryButtonDesign: {
        textKey: 'signup.wixel.primary_button',
        skin: 'standard',
        fullWidth: true,
      },
      alreadyHaveAccount: {
        title: {
          key: 'login.wixel.already_have_account',
        },
      },
      dividerTextKey: 'emailStep.wixel.or.login.with', // "or" divider between social and email
    },
    signup: {
      title: {
        key: 'signup.wixel.title',
      },
      subtitle: {
        key: 'signup.wixel.subtitle',
      },
      primaryButtonDesign: {
        textKey: 'signup.wixel.primary_button',
        skin: 'standard',
        fullWidth: true,
      },
      flows: [
        {
          id: 'image',
          title: {
            key: 'signup.wixel.image_flow.title',
          },
          subtitle: {
            key: 'signup.wixel.image_flow.subtitle',
          },
        },
      ],
      dividerTextKey: 'emailStep.wixel.signup.divider', // "or" divider between social and email
    },
    buttonDesign: {
      skin: 'standard',
      fullWidth: true,
      showArrowOnButton: true,
    },
    titleCustomFontSize: {
      authPage: {
        desktop: 'xLarge',
        mobile: 'xLarge',
      },
      secondaryFlow: {
        desktop: 'xLarge',
        mobile: 'xLarge',
      },
    },
    cssVariables: {
      '--wds-loader-fill-loading': '#D6FFE1',
      '--wds-loader-fill': '#1AAC40',
      '--wds-button-fill-standard-primary': '#442894',
      '--wds-button-fill-standard-primary-hover': '#442894',
      '--wds-button-fill-standard-primary-active': '#442894',
      '--wds-button-fill-standard-secondary-hover': '#442894',
      '--wds-button-fill-standard-secondary-active': '#442894',
      '--wds-color-text-primary': 'black',
      '--wds-font-weight-medium': '400',
      '--wds-font-weight-heading-1': '400',
      '--wds-font-weight-heading-2': '400',
      '--wds-font-weight-heading-3': '400',
      '--wds-color-border-standard-secondary': '#442894',
      '--wds-color-fill-standard-tertiary-hover': '#ffffff',
      '--wds-font-line-height-body-tiny': '18px',
      '--wds-font-family-display': 'Madefor Display',
    },
    background: `linear-gradient(144.34deg, rgba(205, 230, 255, 0.7) 31.48%, #8FF4AA 120.33%), linear-gradient(180deg, #ADD7FF 61.16%, #D3EAFF 100%)`,
    policies: {
      policyKey: 'policies_section.signup.wixel',
    },
    newGoogleSdkSocialButtonDesign: COMMON_NEW_GOOGLE_SDK_SOCIAL_BUTTON_DESIGN,
  },
  [PRESETS.PICASSO]: {
    id: PRESETS.PICASSO,
    shouldDisablePostLoginSuggestions: true,
    nameKey: 'presets.names.wixel',
    container: {
      layout: 'vertical',
      desktop: {
        primary: {
          padding: '48px 0 24px 0',
        },
        secondary: {
          padding: '48px 0 52px 0',
        },
        borderRadius: '24px',
        boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.15)',
      },
      mobile: {
        primary: {
          padding: '36px 0 54px 0',
        },
        secondary: {
          padding: '36px 0 54px 0',
        },
        borderRadius: '24px',
        boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.15)',
      },
    },
    logo: {
      src: 'https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/logos/picasso-logo.png',
      url: 'https://www.wix.com/picasso',
      height: '90px',
      alt: 'Picasso',
      mobile: {
        height: '24px',
      },
    },
    landingPage: {
      title: {
        key: 'emailStep.picasso.title',
      },
      dividerTextKey: 'emailStep.picasso.or.login.with', // "or" divider between social and email
    },
    login: {
      title: {
        key: 'login.picasso.title',
      },
      subtitle: {
        key: 'login.picasso.subtitle',
      },
      primaryButtonDesign: {
        textKey: 'signup.picasso.primary_button',
        skin: 'dark',
        fullWidth: true,
      },
      alreadyHaveAccount: {
        title: {
          key: 'login.picasso.already_have_account',
        },
      },
      dividerTextKey: 'emailStep.picasso.or.login.with', // "or" divider between social and email
    },
    signup: {
      title: {
        key: 'signup.picasso.title',
      },
      subtitle: {
        key: 'signup.picasso.subtitle',
      },
      primaryButtonDesign: {
        textKey: 'signup.picasso.primary_button',
        skin: 'dark',
        fullWidth: true,
      },
      flows: [
        {
          id: 'image',
          title: {
            key: 'signup.picasso.image_flow.title',
          },
          subtitle: {
            key: 'signup.picasso.image_flow.subtitle',
          },
        },
      ],
      dividerTextKey: 'emailStep.picasso.signup.divider', // "or" divider between social and email
    },
    buttonDesign: {
      skin: 'dark',
      fullWidth: true,
      showArrowOnButton: true,
    },
    cssVariables: {
      '--wds-loader-fill-loading': '#D6FFE1',
      '--wds-loader-fill': '#1AAC40',
    },
    background: `url("https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/logos/picasso-background.jpeg")`,
    policies: {
      policyKey: 'policies_section.signup.picasso',
    },
  },
  [PRESETS.WIX_2025]: {
    id: PRESETS.WIX_2025,
    shouldDisablePostLoginSuggestions: false,
    container: {
      layout: 'vertical',
      desktop: {
        primary: {
          padding: '48px 0 24px 0',
        },
        secondary: {
          padding: '48px 0 48px 0',
        },
        borderRadius: '12px',
        boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.15)',
      },
      mobile: {
        fullScreen: true, // Full-screen dialog on mobile (no card styling)
        primary: {
          padding: '12px 48px 36px 48px', // 48px horizontal = 24px card margin + 24px content padding from Figma
        },
        secondary: {
          padding: '12px 48px 36px 48px',
        },
        borderRadius: '0',
        boxShadow: 'none',
      },
    },
    logo: {
      height: '29px',
      mobile: {
        height: '18px',
      },
      dividerColor: '#F1F1F1', // Divider between Wix logo and account logo on SSO screens
    },
    socialButtonDesign: {
      borderColor: '#000000', // Black border per Figma (node 3808-12633)
      borderWidth: '1px', // Per Figma spec
      borderRadius: '6px', // Per Figma spec
      height: '43px',
      iconTextLayout: 'centered',
      fontSize: '14px',
    },
    newGoogleSdkSocialButtonDesign: {
      ...COMMON_NEW_GOOGLE_SDK_SOCIAL_BUTTON_DESIGN,
      height: '40px',
      borderRadius: '4px',
      iconTextLayout: 'centered',
      fontSize: '14px',
    },
    formSpacing: {
      inputButtonGap: '12px', // 12px gap between input and button when no forgot email link (Figma node 3808-12377)
      inputForgotEmailGap: '8px', // 8px gap between input and forgot email link when present
      forgotEmailButtonGap: '24px', // 24px gap between forgot email link and button
      policiesDividerSpacing: {
        top: '44px', // 44px above the horizontal divider (Figma node 3808-12646)
        centerVertically: true, // Center policies text between divider and bottom of card
      },
      titleSectionGap: '16px', // 16px gap between title and subtitle (Figma node 3808-12463)
      enterCodeButtonLayout: 'stacked', // Stacked full-width buttons in 2FA code dialogs (Figma node 3808-12663)
      secondaryDialogInputButtonGap: {
        desktop: '45px', // Total visual gap between input and button
      },
    },
    landingPage: {
      primaryButtonDesign: {
        skin: 'standard',
        fullWidth: true,
        hideSuffixIcon: true, // Hide ChevronRight icon to match Figma
      },
      dividerTextKey: 'emailStep.divider', // "or" divider between social and email
    },
    login: {
      primaryButtonDesign: {
        textKey: 'emailStep.submit', // "Continue with Email"
        skin: 'standard',
        fullWidth: true,
      },
      dividerTextKey: 'emailStep.divider', // "or" divider between social and email
    },
    signup: {
      primaryButtonDesign: {
        textKey: 'emailStep.submit', // "Continue with Email" for first step
        skin: 'standard',
        fullWidth: true,
      },
      emailFormButtonTextKey: 'signup.submit', // "Sign Up" for second step (password form)
      dividerTextKey: 'signup.divider', // "or" divider between social and email
    },
    buttonDesign: {
      skin: 'standard',
      fullWidth: true,
      height: '43px', // Figma spec from node 3808-12631
      fontSize: '14px', // Per Figma spec
      showArrowOnButton: false,
    },
    titleCustomFontSize: {
      authPage: {
        desktop: 'xLarge', // 36px with 16px margin-bottom (Figma node 3808-12631)
        mobile: 'xLarge', // 28px with 12px margin-bottom
      },
      secondaryFlow: {
        desktop: 'xLarge',
        mobile: 'xLarge',
      },
    },
    loaderColors: {
      fullPage: {
        fill: '#D5DFFF',
        fillLoading: '#2F5DFF',
      },
    },
    cssVariables: {
      // Loader colors
      '--wds-loader-fill-loading': '#0F2CCF', // Primary spinner arc color
      '--wds-loader-fill': '#ffffff', // Background spinner arc color

      // Primary Button colors (Figma nodes: 3808-15760, 3808-16879, 3808-16886, 3808-16893)
      '--wds-button-fill-standard-primary': '#2f5dff', // Regular state (node 3808-15760)
      '--wds-button-fill-standard-primary-hover': '#1c3fe5', // Hover (node 3808-16879)
      '--wds-button-fill-standard-primary-active': '#0f2ccf', // Pressed (node 3808-16886)

      // Button border radius - override WDS default pill shape to rounded rectangle
      // Figma shows moderate border radius (node 3808-12628)
      '--wds-button-border-radius-medium': '6px',

      // Font family - Madefor Display for headings (Figma node 3808-12631)
      '--wds-font-family-display': 'Madefor Display',

      // Font weight - Medium weight for headings per 2025 spec
      '--wds-font-weight-heading-1': '500',

      // NOTE: These CSS variables follow the wixel preset pattern.
      // Additional button states (disabled, secondary) and input field colors
      // cannot be configured via CSS variables and require component-level SCSS overrides.
      // See docs/wix-2025-preset-notes.md for details on non-configurable styles.
    },
    // Switch link styling (Sign Up, Log In dialog switch links, Continue with SSO)
    switchLinkDesign: {
      color: '#2F5DFF',
      hoverColor: '#084EBD',
      focusRingColor: '#116DFF',
      underline: 'none',
      fontWeight: '500', // Medium
    },
    // Divider styling (between social buttons and email input)
    dividerDesign: {
      textColor: '#000000',
      lineColor: '#D4D4D4',
    },
    // Policies footer link styling
    policies: {
      linkColor: '#000624',
    },
    // Keep Brand theme enabled for input border colors
    useBrandTheme: true,
    // Override Brand theme's hardcoded secondary text color for policies/reCAPTCHA
    secondaryTextColor: '#42454C',
    // Background: responsive image-set with format fallbacks (AVIF → WebP → JPEG)
    // Browser automatically selects optimal format and resolution based on support and device pixel ratio
    background: `#e4e3e3 image-set(
      url("https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/wix-2025/wix-2025-bg%401x.avif") type("image/avif") 1x,
      url("https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/wix-2025/wix-2025-bg%402x.avif") type("image/avif") 2x,
      url("https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/wix-2025/wix-2025-bg%401x.webp") type("image/webp") 1x,
      url("https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/wix-2025/wix-2025-bg%402x.webp") type("image/webp") 2x,
      url("https://img-wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/wix-2025-bg.png/v1/fit/w_2560,h_1763,q_90/wix-2025-bg.jpeg") 1x,
      url("https://img-wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/wix-2025-bg.png/v1/fit/w_5120,h_3526,q_90/wix-2025-bg.jpeg") 2x
    ) center center/2560px 1763px no-repeat`,
    backButtonPosition: 'aboveDialog',
    mobileBackButtonConfig: {
      position: 'header',
      marginTop: '23px',
      marginStart: '20px',
      iconTextGap: '4px',
    },
    // Dialog vertically centered with minimum top offset
    dialogVerticalPosition: 'centered',
    dialogMinTopOffset: '100px',
  },
};
